import {
  BIRD_FOOTPRINT_DISPLAY_HEIGHT,
  BIRD_FOOTPRINT_DISPLAY_WIDTH,
  BIRD_FOOTPRINT_GLYPH_ID,
  BIRD_FOOTPRINT_STEPS_VIEWBOX_HEIGHT,
  BIRD_FOOTPRINT_STEPS_VIEWBOX_WIDTH,
  BIRD_FOOTPRINT_VIEWBOX_HEIGHT,
  BIRD_FOOTPRINT_VIEWBOX_WIDTH,
} from '@/constants/birdFootprint';

import type { BirdFootprintStep } from '@/constants/birdFootprint';

/** Scale from path viewBox → Figma display size. */
export const FOOTPRINT_SCALE_X =
  BIRD_FOOTPRINT_DISPLAY_WIDTH / BIRD_FOOTPRINT_VIEWBOX_WIDTH;
export const FOOTPRINT_SCALE_Y =
  BIRD_FOOTPRINT_DISPLAY_HEIGHT / BIRD_FOOTPRINT_VIEWBOX_HEIGHT;

const halfW = BIRD_FOOTPRINT_DISPLAY_WIDTH / 2;
const halfH = BIRD_FOOTPRINT_DISPLAY_HEIGHT / 2;

export const TRAIL_ORIGIN_X = BIRD_FOOTPRINT_STEPS_VIEWBOX_WIDTH / 2;
export const TRAIL_ORIGIN_Y = BIRD_FOOTPRINT_STEPS_VIEWBOX_HEIGHT / 2;

/** Extra viewBox padding so rotated wrappers aren’t clipped at the edge. */
const TRAIL_VIEWBOX_PAD = 48;

export function trailViewBox(rotateDeg: number): {
  minX: number;
  minY: number;
  width: number;
  height: number;
} {
  if (rotateDeg === 0) {
    return {
      minX: 0,
      minY: 0,
      width: BIRD_FOOTPRINT_STEPS_VIEWBOX_WIDTH,
      height: BIRD_FOOTPRINT_STEPS_VIEWBOX_HEIGHT,
    };
  }

  const rad = (Math.abs(rotateDeg) * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  const rotatedW =
    BIRD_FOOTPRINT_STEPS_VIEWBOX_WIDTH * cos +
    BIRD_FOOTPRINT_STEPS_VIEWBOX_HEIGHT * sin;
  const rotatedH =
    BIRD_FOOTPRINT_STEPS_VIEWBOX_WIDTH * sin +
    BIRD_FOOTPRINT_STEPS_VIEWBOX_HEIGHT * cos;
  const width = rotatedW + TRAIL_VIEWBOX_PAD * 2;
  const height = rotatedH + TRAIL_VIEWBOX_PAD * 2;

  return {
    minX: TRAIL_ORIGIN_X - width / 2,
    minY: TRAIL_ORIGIN_Y - height / 2,
    width,
    height,
  };
}

export function footprintTransform(print: BirdFootprintStep): string {
  const cx = print.left + print.wrapperWidth / 2;
  const cy = print.top + print.wrapperHeight / 2;
  return `translate(${cx} ${cy}) rotate(${print.rotateDeg}) translate(${-halfW} ${-halfH}) scale(${FOOTPRINT_SCALE_X} ${FOOTPRINT_SCALE_Y})`;
}

export function trailGroupTransform({
  x,
  y,
  rotateDeg,
  scale,
  flipY = false,
}: {
  x: number;
  y: number;
  rotateDeg: number;
  scale: number;
  flipY?: boolean;
}): string | undefined {
  const parts: string[] = [];
  if (x !== 0 || y !== 0) parts.push(`translate(${x} ${y})`);
  if (rotateDeg !== 0) {
    parts.push(`rotate(${rotateDeg} ${TRAIL_ORIGIN_X} ${TRAIL_ORIGIN_Y})`);
  }
  // Match Figma/CSS: rotate then scaleY(-1) around the Steps centre.
  if (flipY) {
    parts.push(
      `translate(${TRAIL_ORIGIN_X} ${TRAIL_ORIGIN_Y}) scale(1 -1) translate(${-TRAIL_ORIGIN_X} ${-TRAIL_ORIGIN_Y})`,
    );
  }
  if (scale !== 1) {
    parts.push(
      `translate(${TRAIL_ORIGIN_X} ${TRAIL_ORIGIN_Y}) scale(${scale}) translate(${-TRAIL_ORIGIN_X} ${-TRAIL_ORIGIN_Y})`,
    );
  }
  return parts.length > 0 ? parts.join(' ') : undefined;
}

/**
 * Shared glyph via document sprite (`BirdFootprintSprite`).
 * Explicit width/height match the symbol viewBox so path units stay 1:1 —
 * without them, `<use>`+symbol defaults to 100% of the trail viewport and
 * paints oversized blobs; `footprintTransform` then scales to Figma display size.
 */
export function FootprintGlyphUse() {
  return (
    <use
      href={`#${BIRD_FOOTPRINT_GLYPH_ID}`}
      width={BIRD_FOOTPRINT_VIEWBOX_WIDTH}
      height={BIRD_FOOTPRINT_VIEWBOX_HEIGHT}
      fill="currentColor"
    />
  );
}

export function FootprintGlyph({ print }: { print: BirdFootprintStep }) {
  return (
    <g transform={footprintTransform(print)}>
      <FootprintGlyphUse />
    </g>
  );
}
